import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader, getAssociateData } from '../services/otpAuthService';
import StepUploadDocumenti from '../components/svincolo-atti/StepUploadDocumenti';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';

const SvincoloAttiUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [atto, setAtto] = useState<any>(null);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('svincolo_upload_tour');

  const tourSteps = [
    {
      target: '#tour-upload-area',
      content: 'Trascina i documenti qui oppure clicca per selezionarli dal tuo dispositivo.',
      skipBeacon: true,
      placement: 'top' as const
    }
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const associateData = getAssociateData();

  // Scroll to top quando la pagina viene caricata
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }

    loadAtto();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [id]);

  const loadAtto = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/atti-svincolo/${id}`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        const attoData = response.data.data;
        
        // Verifica che sia il richiedente
        if (attoData.richiedente._id !== associateData?.id) {
          toast.error('Non hai i permessi per caricare documenti per questa richiesta');
          navigate('/area-riservata/svincolo-atti/lista');
          return;
        }

        // Verifica che uno studio sia stato assegnato
        if (!attoData.studioAssegnato) {
          toast.error('Devi prima accettare un preventivo');
          navigate(`/area-riservata/svincolo-atti/dettaglio/${id}`);
          return;
        }

        // Consentito aggiungere documenti finché la pratica non è chiusa.
        // Blocco solo su stati terminali.
        if (['completata', 'annullata', 'scaduta'].includes(attoData.status)) {
          toast.error('Non è più possibile aggiungere documenti a questa pratica');
          navigate(`/area-riservata/svincolo-atti/dettaglio/${id}`);
          return;
        }

        setAtto(attoData);
      }
    } catch (error: any) {
      console.error('Errore caricamento atto:', error);
      toast.error('Errore durante il caricamento della richiesta');
      navigate('/area-riservata/svincolo-atti/lista');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/area-riservata/svincolo-atti/dettaglio/${id}`);
  };

  const handleSuccess = () => {
    toast.success('Documenti caricati con successo! Lo studio è stato notificato.');
    navigate(`/area-riservata/svincolo-atti/dettaglio/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!atto) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/area-riservata/svincolo-atti/lista')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Torna alla lista
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-base font-bold text-gray-900 dark:text-white">
              Upload Documenti - Targa {atto.targa}
            </h1>
            <button
              onClick={startTour}
              className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
              title="Guida"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Studio selezionato: <span className="font-semibold">{atto.studioAssegnato?.businessName || atto.studioAssegnato?.associateId?.nominativo}</span>
          </p>
        </div>

        {/* Upload Component */}
        <div id="tour-upload-area">
          <StepUploadDocumenti
            attoId={id!}
            onBack={handleBack}
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  );
};

export default SvincoloAttiUploadPage;

